import {
  isCenterpiece,
  TILE_SIZES,
  type TilePreviewSpec,
} from '@mixtiles/web-backend-shared'
import { filterTileParams } from 'components/PhotoBank/tileUtils'
import { type RemotePhoto } from 'components/PhotoBank/PhotoBank.types'
import { type Tile } from 'components/TilesProvider/TilesProvider.types'
import { MIXED_SIZES } from 'pages/PhotoStyler/TileDesignerConsts'
import { getTileStyleData } from 'services/tilesStyling'

export type TilesRestorePayload = {
  tiles: Record<string, unknown>[]
  tileSize: string
  materialType: string
}

const MAX_TILE_PREVIEWS = 5

type TilesPayloadInput = {
  tiles: Tile[]
  remotePhotoByUid: Record<string, RemotePhoto>
  tileSize: string
  materialType: string
}

export function buildTilesRestorePayload({
  tiles,
  remotePhotoByUid,
  tileSize,
  materialType,
}: TilesPayloadInput): TilesRestorePayload {
  return {
    tiles: tiles
      .filter(
        (tile): tile is Tile & { uid: string } =>
          !!tile.uid && !!remotePhotoByUid[tile.uid]
      )
      .map((tile) => ({
        ...filterTileParams(tile),
        ...remotePhotoByUid[tile.uid],
      })),
    tileSize,
    materialType,
  }
}

function tileToPreviewSpec(
  tile: Tile,
  remotePhoto: RemotePhoto,
  tileSize: string,
  materialType: string
): TilePreviewSpec {
  const crop = tile.cropParams[tile.currentAspectRatio]
  return {
    photoUrl: remotePhoto.originalUrl,
    crop: crop && {
      x: crop.x,
      y: crop.y,
      width: crop.width,
      height: crop.height,
    },
    materialType: tile.materialType || materialType,
    frameColor: getTileStyleData(tile).frameColor,
    tileSize:
      tile.tileSize ||
      (tileSize === MIXED_SIZES ? TILE_SIZES.SQUARE_8X8 : tileSize),
  }
}

export function buildTilePreviewSpecs({
  tiles,
  remotePhotoByUid,
  tileSize,
  materialType,
}: TilesPayloadInput): TilePreviewSpec[] {
  return tiles
    .filter(isPreviewableTile(remotePhotoByUid))
    .slice(0, MAX_TILE_PREVIEWS)
    .map((tile) =>
      tileToPreviewSpec(
        tile,
        remotePhotoByUid[tile.uid],
        tileSize,
        materialType
      )
    )
}

const isPreviewableTile =
  (remotePhotoByUid: Record<string, RemotePhoto>) =>
  (tile: Tile): tile is Tile & { uid: string } =>
    !!tile.uid && !isCenterpiece(tile) && !!remotePhotoByUid[tile.uid]
